import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import LiquidGlass from "../LiquidGlass";

function isExternal(to) {
  return /^https?:\/\//.test(to);
}

function NavLink({ item, className, children }) {
  if (isExternal(item.to)) {
    return (
      <a className={className} href={item.to} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }

  return (
    <Link to={item.to} className={className}>
      {children}
    </Link>
  );
}

export default function NavPreviewPanel({ section, pathname, hash, activeTo, popupDark, open, onMouseEnter, onMouseLeave }) {
  // The glass canvas (WebGPU context + ResizeObserver) is expensive to keep
  // alive for all four nav popups at once when at most one is ever visible.
  // Mount it on the first visible render, then leave it mounted so re-opening
  // the same panel doesn't swap implementations while the panel is visible.
  const [hasOpened, setHasOpened] = useState(open);

  useEffect(() => {
    if (open) setHasOpened(true);
  }, [open]);

  if (!section?.items?.length) {
    return null;
  }

  const isProductVariant = section.variant === "product";
  const isAppPage = ["/software", "/product", "/products", "/products/app", "/products/studio"]
    .includes(pathname.replace(/\/$/, ""));
  const previewItems = section.items.filter((item) => !item.hideInPreview);
  const currentHash = (hash || "").replace(/^#/, "");
  const columns = Math.min(3, Math.max(2, Math.ceil(previewItems.length / 2)));
  const SectionIcon = section.icon;

  const shellClassName = `nav-preview-shell ${popupDark ? "nav-preview-shell-dark" : "nav-preview-shell-light"}`;
  const shellStyle = {
    width: isProductVariant
      ? "min(800px, calc(100vw - 48px))"
      : columns === 2
        ? "min(720px, calc(100vw - 48px))"
        : "min(960px, calc(100vw - 48px))",
  };
  const shouldRenderGlass = hasOpened || open;

  const content = (
    <>
      {isProductVariant ? (
          <div className="nav-preview-product">
            <div className="nav-preview-product-feature">
              <div className="nav-preview-product-eyebrow">{section.eyebrow}</div>
              {isAppPage && (
                <>
                  <h3 className="nav-preview-product-title">{section.title}</h3>
                  <p className="nav-preview-product-description">{section.description}</p>
                </>
              )}

              {section.storeLinks?.length ? (
                <div className="nav-preview-product-actions">
                  {section.storeLinks.map((store) => (
                    <NavLink key={store.to} item={store} className="nav-preview-product-cta">
                      <img
                        src={store.logo}
                        alt=""
                        aria-hidden="true"
                        className={`nav-preview-product-cta-logo nav-preview-product-cta-logo-${store.key}`}
                      />
                      {store.label}
                    </NavLink>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="nav-preview-product-links" aria-label="Ateno Studio links">
              {previewItems.map((item) => {
                const itemHash = item.to.includes("#") ? item.to.split("#")[1] : "";
                const itemPath = itemHash ? item.to.split("#")[0] : item.to;
                const Icon = item.icon;
                const active =
                  activeTo === item.to ||
                  (!activeTo && itemHash && pathname === itemPath && currentHash === itemHash) ||
                  (!activeTo && !itemHash && pathname === itemPath);

                return (
                  <NavLink key={item.to} item={item} className={`nav-preview-product-link${active ? " active" : ""}`}>
                    <span className="nav-preview-product-link-head">
                      {Icon ? (
                        <span className="nav-preview-product-link-icon" aria-hidden="true">
                          <Icon size={13} strokeWidth={1.8} />
                        </span>
                      ) : null}
                      <span className="nav-preview-product-link-title">{item.label}</span>
                    </span>
                    {item.subtitle ? (
                      <span className="nav-preview-product-link-subtitle">{item.subtitle}</span>
                    ) : null}
                  </NavLink>
                );
              })}
            </div>

            {section.footerLink ? (
              <div className="nav-preview-product-footer">
                <NavLink item={section.footerLink} className="nav-preview-product-footer-link">
                  {section.footerLink.label} <span aria-hidden="true">-></span>
                </NavLink>
              </div>
            ) : null}
          </div>
        ) : (
          <>
            <div className="nav-preview-header">
              <div className="nav-preview-title-row">
                <span className="nav-preview-title-icon" aria-hidden="true">
                  {SectionIcon ? <SectionIcon size={14} strokeWidth={1.9} /> : null}
                </span>
                <div className="nav-preview-title">{section.label}</div>
              </div>
              <div className="nav-preview-copy">
                {section.preview}
              </div>
            </div>

            <div className="nav-preview-grid" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
              {previewItems.map((item) => {
                const itemHash = item.to.includes("#") ? item.to.split("#")[1] : "";
                const itemPath = itemHash ? item.to.split("#")[0] : item.to;
                const Icon = item.icon;
                const active =
                  activeTo === item.to ||
                  (!activeTo && itemHash && pathname === itemPath && currentHash === itemHash) ||
                  (!activeTo && !itemHash && pathname === itemPath);

                return (
                  <NavLink key={item.to} item={item} className={`nav-preview-item${active ? " active" : ""}`}>
                    <span className="nav-preview-item-icon" aria-hidden="true">
                      {Icon ? <Icon size={13} strokeWidth={1.8} /> : null}
                    </span>
                    <span className="nav-preview-item-label">{item.label}</span>
                    <span className="nav-preview-item-arrow" aria-hidden="true">-></span>
                  </NavLink>
                );
              })}
            </div>
          </>
        )}
    </>
  );

  return (
    <div
      className={`nav-preview-panel${open ? " open" : ""}`}
      aria-hidden={!open}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {shouldRenderGlass ? (
        <LiquidGlass className={shellClassName} tone={popupDark ? "dark" : "light"} cornerRadius={26} style={shellStyle}>
          {content}
        </LiquidGlass>
      ) : (
        <div className={shellClassName} style={shellStyle}>
          {content}
        </div>
      )}
    </div>
  );
}
